import type { KeyboardEvent } from 'react';

import { Fragment, useMemo, useState } from 'react';

import { Blank, PageHead, Pager, SearchBox } from 'views/design-system';
import { fmtDate } from 'views/design-system/format';

import { currentPeriod, fmtDocs, money, periodLabel } from './derive';
import { useTransferCharges } from './use-transfer-charges';
import { useViewCopy } from './use-view-copy';

import 'views/design-system/tokens.scss';
import 'views/design-system/surface.scss';
import './evault-deck.scss';

/**
 * eVAULT TRANSFERS — the billed ownership transfers, on their own page.
 *
 * It was a tab on the billing page, which put a record of eVault activity
 * behind a payments screen and one click past a tab strip about invoices. The
 * billing page keeps what it is actually for: the add-on row (now with a link
 * here) and the invoice figures, which still fold this cycle's transfer total
 * into month-to-date and the metered card.
 *
 * Nothing here recomputes a price. Every figure — the per-row amount, the
 * document count, the footer total — is the server's, so this page and the
 * invoice cannot disagree.
 */
/** The same page size the billing tab paged this table at. */
const PAGE = 10;

export const TransfersDeck = () => {
	const transfers = useTransferCharges();
	/* The admin can see the names of documents they transferred away; this is
	   what lets them read one. The copy is watermarked and rendered per
	   request — see `use-view-copy`. */
	const { viewCopy, busyId, ready } = useViewCopy();
	/* Which row is expanded to its document list — one at a time, like every
	   other disclosure in this deck. */
	const [openTransfer, setOpenTransfer] = useState<string | null>(null);
	const [q, setQ] = useState('');
	const [page, setPage] = useState(1);

	/* THE CURRENT MONTH IS ALWAYS THE FIRST OPTION.
	   `periods` only carries a month the business already has a charge in — a
	   business with none this month would otherwise offer a picker with nothing
	   selectable, and the page would have no way to say "no transfers YET" for
	   the month that matters. Prepending it unconditionally, and value
	   `'current'` rather than the literal month, is what keeps the query in step
	   with the endpoint's own `period=current` shorthand. */
	const periodOptions = useMemo(() => {
		const now = currentPeriod();
		const older = transfers.periods.filter(p => p !== now);
		return [
			{ value: 'current', label: `${periodLabel(now)} (current)` },
			...older.map(p => ({ value: p, label: periodLabel(p) })),
		];
	}, [transfers.periods]);

	/* THE BILLING TAB'S OWN PREDICATE, moved with the table.
	   Recipient name/email, or ANY document name — someone hunting a filename
	   should find the transfer without knowing who it went to. Client-side,
	   because the endpoint is period-scoped and returns the whole period. */
	const rows = useMemo(() => {
		const needle = q.trim().toLowerCase();
		if (!needle) return transfers.rows;
		return transfers.rows.filter(t =>
			`${t.recipient?.name ?? ''} ${t.recipient?.email ?? ''} ${(t.documents ?? [])
				.map(doc => doc.name)
				.join(' ')}`
				.toLowerCase()
				.includes(needle)
		);
	}, [q, transfers.rows]);

	const pages = Math.max(1, Math.ceil(rows.length / PAGE));
	/* Clamped rather than reset by an effect: a search that shortens the list
	   below the current page would otherwise render an empty table for a beat
	   before the effect fired. */
	const shown = useMemo(() => {
		const safe = Math.min(page, pages);
		return rows.slice((safe - 1) * PAGE, safe * PAGE);
	}, [rows, page, pages]);

	return (
		<div className="sv2-scope sv2 evault-deck transfers-deck">
			<PageHead
				title="Transfers"
				sub="Every ownership transfer this cycle, who received it, and what it cost."
			/>

			<div className="ctl">
				<SearchBox
					value={q}
					onChange={v => {
						setQ(v);
						setPage(1);
						setOpenTransfer(null);
					}}
					placeholder="Search transfers"
					title="Searches recipient name, email and document names"
				/>
				<select
					className="range"
					aria-label="Billing period"
					value={transfers.period}
					onChange={e => {
						transfers.setPeriod(e.target.value);
						setPage(1);
						setOpenTransfer(null);
					}}
				>
					{periodOptions.map(o => (
						<option key={o.value} value={o.value}>
							{o.label}
						</option>
					))}
				</select>
				<Pager page={Math.min(page, pages)} pages={pages} onChange={setPage} />
			</div>

			<div className="panel">
				{/* Gated on `isLoaded` — `rows` still holds the PREVIOUS period's
				    rows for a beat after a picker change, since the hook clears
				    `isLoaded` up front but not the rows themselves. Without this
				    the table flashes the old period before the new fetch lands,
				    and the Blank below flashes "no transfers" in the gap. */}
				{!transfers.isLoaded ? (
					<div className="sv2-loading">Loading transfers…</div>
				) : rows.length ? (
					<div className="tscroll">
						<table className="stbl evault-tbl">
							<thead>
								<tr>
									<th>Date</th>
									<th>Documents</th>
									<th>Recipient</th>
									<th className="num">Amount (USD)</th>
								</tr>
							</thead>
							<tbody>
								{shown.map(t => {
									const id = t._id ?? t.transferId;
									/* The column is the SERVER's own count — `t.quantity`, what
									   was actually charged — not `t.documents.length`, which can
									   be thin or stale on a row `documents` was not fully
									   populated for. `fmtDocs` supplies the fallback and the list
									   the expansion renders, derived once. */
									const docs = fmtDocs(t.documents);
									const qty = Number(t.quantity ?? docs.count);
									const qtyLabel = `${qty} document${qty === 1 ? '' : 's'}`;
									const open = openTransfer === id;
									const toggle = () =>
										setOpenTransfer(o => (o === id ? null : id ?? null));
									/* The same two keys the vault and document rows take. The
									   guard leaves anything focusable INSIDE the row acting for
									   itself, and Space has to be prevented or the page scrolls
									   out from under the reader as well. */
									const onRowKey = (e: KeyboardEvent<HTMLTableRowElement>) => {
										if (e.target !== e.currentTarget) return;
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											toggle();
										}
									};
									return (
										<Fragment key={id}>
											<tr
												aria-expanded={open}
												tabIndex={0}
												onClick={toggle}
												onKeyDown={onRowKey}
											>
												<td>
													{t.committedAt
														? fmtDate(new Date(t.committedAt).getTime())
														: '—'}
												</td>
												<td>
													<span aria-hidden="true">{open ? '▾' : '▸'}</span>{' '}
													{qtyLabel}
												</td>
												<td>
													{t.recipient?.name ? (
														<>
															<span className="nm">{t.recipient.name}</span>
															<br />
															<span className="mail">
																{t.recipient.email || '—'}
															</span>
														</>
													) : (
														<span className="nm">
															{t.recipient?.email || '—'}
														</span>
													)}
													{/* THE LEGAL OWNER the original went to, under the
													    person who received it. A charge row written
													    before the entity was recorded has none and
													    simply shows the recipient, as it always did —
													    this page bills transfers, so it is not the
													    place to raise a NOT ESTABLISHED finding. */}
													{t.recipient?.entity?.name ? (
														<>
															<br />
															<span className="mail">
																{t.recipient.entity.name}
															</span>
														</>
													) : null}
												</td>
												<td className="num">{money(t.amount)}</td>
											</tr>
											{open && (
												<tr className="evault-expand">
													<td />
													<td colSpan={3}>
														{docs.items.length ? (
															<ul className="evault-doclist" role="list">
																{docs.items.map(doc => (
																	<li key={doc.id ?? doc.name}>
																		<span className="docname">{doc.name}</span>
																		{/* ONLY WHERE A COPY WAS ACTUALLY KEPT. A
																		    transfer made without the retention copy
																		    leaves the initiator nothing to open, and an
																		    id is not an entitlement — a button there
																		    would open a tab and close it again. A row
																		    written before the flag existed has no
																		    `retained` and gets no button. */}
																		{doc.id && doc.retained === true ? (
																			<button
																				className="dbtn"
																				type="button"
																				disabled={!ready || !!busyId}
																				title="Opens the watermarked copy in a new tab"
																				onClick={() =>
																					void viewCopy(doc.id, doc.name)
																				}
																			>
																				View document
																			</button>
																		) : null}
																	</li>
																))}
															</ul>
														) : (
															<span className="kv__none">
																No documents on this transfer
															</span>
														)}
													</td>
												</tr>
											)}
										</Fragment>
									);
								})}
							</tbody>
							<tfoot className="inv-foot">
								<tr>
									<td colSpan={4}>
										{transfers.rows.length} transfer
										{transfers.rows.length === 1 ? '' : 's'} ·{' '}
										{transfers.totals.quantity} document
										{transfers.totals.quantity === 1 ? '' : 's'} ·{' '}
										<b>${money(transfers.totals.amount)}</b> this cycle
									</td>
								</tr>
							</tfoot>
						</table>
					</div>
				) : transfers.rows.length ? (
					/* A SEARCH MISS IS NOT AN EMPTY PERIOD. The period has charges;
					   this query does not match them, and saying "no transfers this
					   cycle" over a non-empty cycle is a lie the reader would have to
					   clear the box to disprove. */
					<div className="sv2-empty hint">No transfers match this search.</div>
				) : (
					<Blank
						title="No ownership transfers this cycle"
						description="A charge appears here the moment a vaulted document's ownership is transferred and confirmed."
					/>
				)}
			</div>
		</div>
	);
};

export default TransfersDeck;
